import React, { useEffect, useState, useMemo } from 'react';
import { getErrorMessage, formatCurrency } from '@/lib/utils';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Puzzle, 
  Search, 
  Building2, 
  Boxes, 
  Truck, 
  CheckCircle2, 
  Clock, 
  XCircle,
  Users,
  Store,
  TrendingUp,
  AlertTriangle,
  ChevronRight,
  Loader2,
  CreditCard,
  Bot,
  UsersRound,
  Package,
  Calendar,
  RefreshCw,
  Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icone per estensioni
const EXTENSION_ICONS: Record<string, any> = {
  ext_stock: Boxes,
  ext_logistics: Truck,
  ext_multisito: Building2,
  multi_user: UsersRound,
  chatbot_ai: Bot,
  bundle_buyer: Package,
  bundle_all: Package
};

const getExtensionIcon = (code: string) => {
  return EXTENSION_ICONS[code] || Puzzle;
};

// Componente KPI Card
const KPICard = ({ 
  icon: Icon, 
  label, 
  value, 
  subValue, 
  color = 'primary' 
}: { 
  icon: any; 
  label: string; 
  value: string | number; 
  subValue?: string;
  color?: string;
}) => {
  const colorClasses: Record<string, string> = {
    primary: 'bg-primary/10 text-primary',
    green: 'bg-green-500/10 text-green-600',
    amber: 'bg-amber-500/10 text-amber-600',
    blue: 'bg-blue-500/10 text-blue-600'
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subValue && <p className="text-xs text-muted-foreground">{subValue}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Componente Badge Stato Estensione
const ExtensionStatusBadge = ({ 
  status, 
  isEnabled, 
  daysRemaining,
  hasCard 
}: { 
  status: string; 
  isEnabled: boolean;
  daysRemaining?: number;
  hasCard?: boolean;
}) => {
  if (!isEnabled || status === 'disabled') {
    return (
      <Badge variant="outline" className="bg-muted/50 text-muted-foreground">
        <XCircle className="h-3 w-3 mr-1" />
        Non attivo
      </Badge>
    );
  }

  if (status === 'trial') {
    const isUrgent = daysRemaining !== undefined && daysRemaining <= 3;
    return (
      <Badge 
        variant="outline" 
        className={isUrgent 
          ? "bg-red-500/10 text-red-600 border-red-500/30" 
          : "bg-amber-500/10 text-amber-600 border-amber-500/30"
        }
      >
        <Clock className="h-3 w-3 mr-1" />
        Trial {daysRemaining !== undefined ? `${daysRemaining}g` : ''}
        {hasCard && <CreditCard className="h-3 w-3 ml-1" />}
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
      <CheckCircle2 className="h-3 w-3 mr-1" />
      Attivo
    </Badge>
  );
};

// Componente Card Utente
const UserExtensionCard = ({ 
  user, 
  allExtensions,
  onManage 
}: { 
  user: any; 
  allExtensions: any[];
  onManage: (user: any) => void;
}) => {
  const roleLabel = user.role === 'seller' ? 'SELLER' : 'BUYER';
  const roleColor = user.role === 'seller' ? 'bg-blue-500/10 text-blue-600' : 'bg-purple-500/10 text-purple-600';
  const RoleIcon = user.role === 'seller' ? Store : Users;

  // Estensioni disponibili per questo ruolo
  const availableExtensions = allExtensions.filter(ext => {
    const target = ext.target || 'seller';
    return target === user.role || target === 'both';
  });

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`user-card-${user.user_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Header utente */}
            <div className="flex items-center gap-2 mb-2">
              <RoleIcon className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold truncate">{user.company_name}</span>
              <Badge variant="outline" className={roleColor}>
                {roleLabel}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-3 truncate">{user.email}</p>

            {/* Griglia estensioni */}
            <div className="flex flex-wrap gap-2">
              {availableExtensions.map(ext => {
                const userExt = user.extensions?.find((e: any) => e.code === ext.extension);
                const Icon = getExtensionIcon(ext.extension);
                const isActive = userExt?.enabled && userExt?.status !== 'disabled';
                
                return (
                  <div 
                    key={ext.extension}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs ${
                      isActive 
                        ? userExt?.status === 'trial'
                          ? 'bg-amber-500/10 text-amber-700 border border-amber-500/30'
                          : 'bg-green-500/10 text-green-700 border border-green-500/30'
                        : 'bg-muted/50 text-muted-foreground border border-transparent'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{ext.name_it?.split(' ')[0]}</span>
                    {userExt?.status === 'trial' && userExt?.days_remaining !== undefined && (
                      <span className="font-medium">{userExt.days_remaining}g</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pulsante gestisci */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onManage(user)}
            className="shrink-0"
            data-testid={`manage-btn-${user.user_id}`}
          >
            Gestisci
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Componente Trial Card (per tab Trial in Scadenza)
const TrialCard = ({ 
  trial, 
  urgencyLevel,
  onClick
}: { 
  trial: any; 
  urgencyLevel: 'urgent' | 'upcoming' | 'later';
  onClick?: () => void;
}) => {
  const urgencyColors = {
    urgent: 'border-l-4 border-l-red-500 bg-red-50 dark:bg-red-950/20',
    upcoming: 'border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-950/20',
    later: 'border-l-4 border-l-blue-500 bg-blue-50 dark:bg-blue-950/20'
  };

  const Icon = getExtensionIcon(trial.extension_code);
  const RoleIcon = trial.role === 'seller' ? Store : Users;

  return (
    <div 
      className={`p-3 rounded-lg ${urgencyColors[urgencyLevel]} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
      data-testid={`trial-card-${trial.user_id}-${trial.extension_code}`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-1.5 rounded bg-background">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium truncate">{trial.company_name}</span>
              <RoleIcon className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground truncate">{trial.extension_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {trial.has_card && (
            <Badge variant="outline" className="bg-green-500/10 text-green-600 text-xs">
              <CreditCard className="h-3 w-3 mr-1" />
              Carta
            </Badge>
          )}
          <Badge 
            variant={urgencyLevel === 'urgent' ? 'destructive' : 'secondary'}
            className="whitespace-nowrap"
          >
            {trial.days_remaining <= 0 
              ? 'Scaduto' 
              : trial.days_remaining === 1 
                ? 'Domani' 
                : `${trial.days_remaining} giorni`
            }
          </Badge>
          {onClick && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </div>
      </div>
    </div>
  );
};

// Componente principale
const AdminExtensionsPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [expiringTrials, setExpiringTrials] = useState<any>(null);
  const [allExtensions, setAllExtensions] = useState<any[]>([]);
  
  // Filtri
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedExtension, setSelectedExtension] = useState('all');
  
  // Dialog gestione
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [statsRes, usersRes, trialsRes, configRes] = await Promise.all([
        axios.get(`${API}/extensions/admin/stats`),
        axios.get(`${API}/extensions/admin/all-users`),
        axios.get(`${API}/extensions/admin/trials-expiring`),
        axios.get(`${API}/extensions/config`)
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setExpiringTrials(trialsRes.data);
      setAllExtensions(configRes.data.all_extensions || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Dati aggiornati');
  };

  // Filtra utenti
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Filtro ricerca
      if (search) {
        const searchLower = search.toLowerCase();
        if (!user.company_name?.toLowerCase().includes(searchLower) && 
            !user.email?.toLowerCase().includes(searchLower)) {
          return false;
        }
      }
      
      // Filtro ruolo
      if (roleFilter !== 'all' && user.role !== roleFilter) {
        return false;
      }
      
      // Filtro stato
      if (statusFilter === 'active') {
        if (!user.extensions?.some((e: any) => e.enabled && e.status === 'active')) {
          return false;
        }
      } else if (statusFilter === 'trial') {
        if (!user.extensions?.some((e: any) => e.enabled && e.status === 'trial')) {
          return false;
        }
      } else if (statusFilter === 'none') {
        if (user.active_count > 0) {
          return false;
        }
      }
      
      // Filtro estensione specifica
      if (selectedExtension !== 'all') {
        if (!user.extensions?.some((e: any) => e.code === selectedExtension && e.enabled)) {
          return false;
        }
      }
      
      return true;
    });
  }, [users, search, roleFilter, statusFilter, selectedExtension]);

  // Gestione attivazione/disattivazione
  const handleActivate = async (userId: string, extension: string, isTrial: boolean) => {
    setActionLoading(`${extension}-activate`);
    try {
      await axios.post(`${API}/extensions/admin/activate`, {
        seller_id: userId,
        extension,
        is_trial: isTrial
      });
      toast.success(isTrial ? 'Trial avviato' : 'Estensione attivata');
      await fetchAllData();
      // Aggiorna selected user
      const updatedUsers = await axios.get(`${API}/extensions/admin/all-users`);
      const updatedUser = updatedUsers.data.users?.find((u: any) => u.user_id === userId);
      if (updatedUser) setSelectedUser(updatedUser);
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'attivazione"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (userId: string, extension: string) => {
    setActionLoading(`${extension}-deactivate`);
    try {
      await axios.post(`${API}/extensions/admin/deactivate`, {
        seller_id: userId,
        extension
      });
      toast.success('Estensione disattivata');
      await fetchAllData();
      // Aggiorna selected user
      const updatedUsers = await axios.get(`${API}/extensions/admin/all-users`);
      const updatedUser = updatedUsers.data.users?.find((u: any) => u.user_id === userId);
      if (updatedUser) setSelectedUser(updatedUser);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella disattivazione'));
    } finally {
      setActionLoading(null);
    }
  };

  // Gestione click su trial card - cerca l'utente e apre il dialog
  const handleTrialClick = (trial: any) => {
    // Cerca l'utente nella lista
    const user = users.find(u => u.user_id === trial.user_id);
    if (user) {
      setSelectedUser(user);
      setShowManageDialog(true);
    } else {
      // Se l'utente non è nella lista, crea un oggetto minimo
      setSelectedUser({
        user_id: trial.user_id,
        email: trial.email,
        company_name: trial.company_name,
        role: trial.role,
        extensions: [{
          code: trial.extension_code,
          name: trial.extension_name,
          status: 'trial',
          enabled: true,
          is_trial: true,
          days_remaining: trial.days_remaining,
          has_card: trial.has_card
        }],
        active_count: 1
      });
      setShowManageDialog(true);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-extensions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
            <Puzzle className="h-8 w-8" />
            Dashboard Abbonamenti
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestisci le estensioni attive per tutti gli utenti
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={refreshData} 
          disabled={refreshing}
          data-testid="refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard 
          icon={Users} 
          label="Utenti Totali" 
          value={stats?.total_users || 0}
          subValue={`${stats?.total_sellers || 0} seller, ${stats?.total_buyers || 0} buyer`}
          color="primary"
        />
        <KPICard 
          icon={CheckCircle2} 
          label="Estensioni Attive" 
          value={stats?.active_subscriptions || 0}
          color="green"
        />
        <KPICard 
          icon={Clock} 
          label="Trial in Corso" 
          value={stats?.active_trials || 0}
          subValue={expiringTrials?.urgent?.length > 0 ? `${expiringTrials.urgent.length} urgenti` : undefined}
          color="amber"
        />
        <KPICard 
          icon={TrendingUp} 
          label="MRR Stimato" 
          value={formatCurrency(stats?.mrr || 0)}
          subValue="Mensile"
          color="blue"
        />
      </div>

      {/* Tabs principali */}
      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users" className="gap-2" data-testid="tab-users">
            <Users className="h-4 w-4" />
            Per Utente
          </TabsTrigger>
          <TabsTrigger value="extensions" className="gap-2" data-testid="tab-extensions">
            <Puzzle className="h-4 w-4" />
            Per Estensione
          </TabsTrigger>
          <TabsTrigger value="trials" className="gap-2" data-testid="tab-trials">
            <AlertTriangle className="h-4 w-4" />
            Trial in Scadenza
            {expiringTrials?.urgent?.length > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {expiringTrials.urgent.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* TAB: Per Utente */}
        <TabsContent value="users" className="space-y-4">
          {/* Filtri */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Cerca utente..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9"
                      data-testid="search-input"
                    />
                  </div>
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-[140px]" data-testid="role-filter">
                    <SelectValue placeholder="Ruolo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i ruoli</SelectItem>
                    <SelectItem value="seller">Solo Seller</SelectItem>
                    <SelectItem value="buyer">Solo Buyer</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[160px]" data-testid="status-filter">
                    <SelectValue placeholder="Stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli stati</SelectItem>
                    <SelectItem value="active">Con estensioni attive</SelectItem>
                    <SelectItem value="trial">In trial</SelectItem>
                    <SelectItem value="none">Senza estensioni</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Lista utenti */}
          <div className="space-y-3">
            {filteredUsers.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun utente trovato con i filtri selezionati</p>
                </CardContent>
              </Card>
            ) : (
              filteredUsers.map(user => (
                <UserExtensionCard 
                  key={user.user_id}
                  user={user}
                  allExtensions={allExtensions}
                  onManage={(u) => {
                    setSelectedUser(u);
                    setShowManageDialog(true);
                  }}
                />
              ))
            )}
          </div>
          
          <p className="text-sm text-muted-foreground text-center">
            Mostrati {filteredUsers.length} di {users.length} utenti
          </p>
        </TabsContent>

        {/* TAB: Per Estensione */}
        <TabsContent value="extensions" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {allExtensions.map(ext => {
              const count = stats?.extensions_count?.[ext.extension] || 0;
              const Icon = getExtensionIcon(ext.extension);
              const targetLabel = ext.target === 'seller' ? 'Seller' : ext.target === 'buyer' ? 'Buyer' : 'Tutti';
              
              return (
                <Card 
                  key={ext.extension} 
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => {
                    setSelectedExtension(ext.extension);
                    // Switcha al tab utenti con filtro
                    const tabsTrigger = document.querySelector('[data-testid="tab-users"]') as HTMLElement;
                    if (tabsTrigger) tabsTrigger.click();
                  }}
                  data-testid={`ext-card-${ext.extension}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{ext.name_it}</h3>
                          <p className="text-sm text-muted-foreground">{targetLabel}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">{count}</p>
                        <p className="text-xs text-muted-foreground">utenti</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        {formatCurrency(ext.monthly_price)}/mese
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* TAB: Trial in Scadenza */}
        <TabsContent value="trials" className="space-y-6">
          {expiringTrials?.total === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <p className="font-medium">Nessun trial in scadenza</p>
                <p className="text-sm">Tutti i trial sono a più di 14 giorni dalla scadenza</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Urgenti */}
              {expiringTrials?.urgent?.length > 0 && (
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-red-600 mb-3">
                    <AlertTriangle className="h-5 w-5" />
                    Urgente (≤ 3 giorni)
                  </h3>
                  <div className="space-y-2">
                    {expiringTrials.urgent.map((trial: any, idx: number) => (
                      <TrialCard 
                        key={`urgent-${idx}`} 
                        trial={trial} 
                        urgencyLevel="urgent"
                        onClick={() => handleTrialClick(trial)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Prossimi */}
              {expiringTrials?.upcoming?.length > 0 && (
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-amber-600 mb-3">
                    <Clock className="h-5 w-5" />
                    Prossimi (4-7 giorni)
                  </h3>
                  <div className="space-y-2">
                    {expiringTrials.upcoming.map((trial: any, idx: number) => (
                      <TrialCard 
                        key={`upcoming-${idx}`} 
                        trial={trial} 
                        urgencyLevel="upcoming"
                        onClick={() => handleTrialClick(trial)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Successivi */}
              {expiringTrials?.later?.length > 0 && (
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-blue-600 mb-3">
                    <Calendar className="h-5 w-5" />
                    Successivi (8-14 giorni)
                  </h3>
                  <div className="space-y-2">
                    {expiringTrials.later.map((trial: any, idx: number) => (
                      <TrialCard 
                        key={`later-${idx}`} 
                        trial={trial} 
                        urgencyLevel="later"
                        onClick={() => handleTrialClick(trial)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog Gestione Utente */}
      <Dialog open={showManageDialog} onOpenChange={setShowManageDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedUser?.role === 'seller' ? <Store className="h-5 w-5" /> : <Users className="h-5 w-5" />}
              {selectedUser?.company_name}
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.email} • {selectedUser?.role?.toUpperCase()}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <h4 className="font-medium text-sm text-muted-foreground">Gestione Estensioni</h4>
            
            {allExtensions
              .filter(ext => {
                const target = ext.target || 'seller';
                return target === selectedUser?.role || target === 'both';
              })
              .map(ext => {
                const userExt = selectedUser?.extensions?.find((e: any) => e.code === ext.extension);
                const isActive = userExt?.enabled && userExt?.status !== 'disabled';
                const isTrial = userExt?.status === 'trial';
                const Icon = getExtensionIcon(ext.extension);
                
                return (
                  <div 
                    key={ext.extension}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isActive ? 'bg-primary/10' : 'bg-muted'}`}>
                        <Icon className={`h-4 w-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <p className="font-medium">{ext.name_it}</p>
                        <p className="text-xs text-muted-foreground">{formatCurrency(ext.monthly_price)}/mese</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <ExtensionStatusBadge 
                        status={userExt?.status || 'disabled'}
                        isEnabled={isActive}
                        daysRemaining={userExt?.days_remaining}
                        hasCard={userExt?.has_card}
                      />
                      
                      {isActive ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeactivate(selectedUser.user_id, ext.extension)}
                          disabled={actionLoading === `${ext.extension}-deactivate`}
                        >
                          {actionLoading === `${ext.extension}-deactivate` ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Disattiva'
                          )}
                        </Button>
                      ) : (
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleActivate(selectedUser.user_id, ext.extension, true)}
                            disabled={actionLoading?.includes(ext.extension)}
                          >
                            {actionLoading === `${ext.extension}-activate` ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              'Trial'
                            )}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleActivate(selectedUser.user_id, ext.extension, false)}
                            disabled={actionLoading?.includes(ext.extension)}
                          >
                            Attiva
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManageDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminExtensionsPage;
